import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ControlContainer, ReactiveFormsModule } from '@angular/forms';
import { NgIf } from '@angular/common';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectButtonModule } from 'primeng/selectbutton';
import { CalendarModule } from 'primeng/calendar';
import { OfferTargetAudienceFormGroup } from '@app/areas/offers/services/offer.form.service';
import {
  AudienceSizeType,
  DateRangePreset,
  RankingMetric,
} from '@app/areas/offers/forms/offer-audience-form/offer-audience-filter.types';
import { Subscription } from 'rxjs';

interface SelectOption<T> {
  label: string;
  value: T;
}

@Component({
  selector: 'app-offer-audience-top-consumers-form',
  templateUrl: './offer-audience-top-consumers-form.component.html',
  styleUrls: ['./offer-audience-top-consumers-form.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, NgIf, InputNumberModule, SelectButtonModule, CalendarModule],
})
export class OfferAudienceTopConsumersFormComponent implements OnInit, OnDestroy {
  private readonly controlContainer = inject(ControlContainer);
  private datePresetSub?: Subscription;
  private dateStartSub?: Subscription;
  private dateEndSub?: Subscription;

  protected topConsumersForm: OfferTargetAudienceFormGroup['controls']['TopConsumersConfig'] | null = null;
  protected today = new Date();

  protected rankingMetricOptions: SelectOption<RankingMetric>[] = [
    { label: 'Number of Visits', value: 'visitCount' },
    { label: 'Total Spend', value: 'totalSpend' },
  ];

  protected audienceSizeOptions: SelectOption<AudienceSizeType>[] = [
    { label: 'Top 10%', value: 'top10' },
    { label: 'Top 20%', value: 'top20' },
    { label: 'Choose Audience Size', value: 'custom' },
  ];

  protected dateRangeOptions: SelectOption<DateRangePreset>[] = [
    { label: 'Last 90 Days', value: 'last90days' },
    { label: 'Last 30 Days', value: 'last30days' },
  ];

  ngOnInit(): void {
    this.topConsumersForm = this.controlContainer
      .control as OfferTargetAudienceFormGroup['controls']['TopConsumersConfig'];

    // Sync date picker when preset changes
    this.datePresetSub = this.topConsumersForm.controls.DateRangePreset.valueChanges.subscribe((preset) => {
      this.syncDatePickerFromPreset(preset);
    });

    // When user manually changes date picker, deselect preset
    this.dateStartSub = this.topConsumersForm.controls.DateRangeStart.valueChanges.subscribe(() => {
      if (this.topConsumersForm?.controls.DateRangePreset.value !== 'custom') {
        this.topConsumersForm?.controls.DateRangePreset.setValue('custom', { emitEvent: false });
      }
    });
    this.dateEndSub = this.topConsumersForm.controls.DateRangeEnd.valueChanges.subscribe(() => {
      if (this.topConsumersForm?.controls.DateRangePreset.value !== 'custom') {
        this.topConsumersForm?.controls.DateRangePreset.setValue('custom', { emitEvent: false });
      }
    });

    // Initialize date picker from default preset
    this.syncDatePickerFromPreset(this.topConsumersForm.controls.DateRangePreset.value);
  }

  ngOnDestroy(): void {
    this.datePresetSub?.unsubscribe();
    this.dateStartSub?.unsubscribe();
    this.dateEndSub?.unsubscribe();
  }

  get isCustomAudienceSize(): boolean {
    return this.topConsumersForm?.controls.AudienceSizeType.value === 'custom';
  }

  private syncDatePickerFromPreset(preset: DateRangePreset): void {
    if (!this.topConsumersForm || preset === 'custom') return;

    const now = new Date();
    let start: Date | null = null;
    const end = new Date(now);

    switch (preset) {
      case 'last30days':
        start = new Date(now);
        start.setDate(start.getDate() - 30);
        break;
      case 'last90days':
        start = new Date(now);
        start.setDate(start.getDate() - 90);
        break;
    }

    this.topConsumersForm.controls.DateRangeStart.setValue(start, { emitEvent: false });
    this.topConsumersForm.controls.DateRangeEnd.setValue(end, { emitEvent: false });
  }
}
